/**
 * Одно место, где решается, работает ли аналитика вообще.
 *
 * ПРАВИЛО. Пустой Measurement ID — это не «аналитика в тихом режиме», а её
 * полное отсутствие: `<script>` не вставляется, `dataLayer` не заводится, к
 * Google не уходит ни одного запроса. Так собирается локальная разработка и
 * так же ведут себя тесты — иначе каждый прогон `npm test` стучался бы в чужой
 * счётчик, а каждый `npm run dev` подмешивал бы разработчика в статистику.
 *
 * ИМЕНА. `ACHP_*`, а не `GA_*`. Прод-хост и группа GitLab общие с Netty, у
 * которого своя аналитика; переменная с общим именем на уровне ГРУППЫ молча
 * перекрыла бы нашу — и хиты этого приложения ушли бы в чужой счётчик, где их
 * никто не ищет. Собственное имя делает такую подмену невозможной.
 *
 * ГДЕ ЗАДАЁТСЯ. Идентификатор публичен — он виден в каждом хите, который шлёт
 * браузер, — поэтому лежит прямо здесь, в коде, а не в секретах. Переменная
 * сборки `VITE_ACHP_GA_MEASUREMENT_ID` его перекрывает: так можно направить
 * прогон в тестовое свойство, не трогая репозиторий, а переменной CI/CD уровня
 * ПРОЕКТА `ACHP_GA_MEASUREMENT_ID` — поменять счётчик без правки кода.
 */

/**
 * Заглушка из постановки. Если она доедет до конфига вместо настоящего
 * идентификатора, аналитика обязана остаться ВЫКЛЮЧЕННОЙ: счётчика `G-XXXXXXXXXX`
 * не существует, и приложение стучалось бы в Google на каждой странице, чтобы
 * каждый раз получить отказ. Отдельная проверка, потому что по форме строка
 * законна — заглушку от настоящего идентификатора отличает только смысл.
 */
const PLACEHOLDER = 'G-XXXXXXXXXX'

/**
 * Форма Measurement ID у GA4: `G-` и дальше заглавные буквы с цифрами.
 * Проверяется, потому что опечатка в переменной CI иначе выглядела бы как
 * работающая аналитика без единого события в отчётах.
 */
const SHAPE = /^G-[A-Z0-9]{6,20}$/

/**
 * Счётчик hub4psy.com. Свойство `HUB4PSY`, поток `HUB4PSY web`.
 *
 * Одно свойство на сайт: в этом неймспейсе GitLab живут соседние проекты со
 * своими счётчиками, и поток, заведённый внутри чужого свойства, смешал бы
 * трафик двух сайтов в одних отчётах — а разделить его задним числом нельзя.
 */
export const SHIPPED_MEASUREMENT_ID = 'G-K6J0CSPTQD'

/**
 * В разработке зашитый идентификатор НЕ применяется.
 *
 * Иначе `npm run dev` слал бы настоящие хиты с localhost в настоящее свойство,
 * и первые недели статистики состояли бы из нас самих. Нужно проверить саму
 * аналитику — задайте `VITE_ACHP_GA_MEASUREMENT_ID` явно (в `.env.local` или в
 * командной строке), лучше со своим тестовым счётчиком. Тесты идут в том же
 * режиме и задают переменную сами, поэтому от этой строки не зависят.
 */
const FALLBACK = import.meta.env.DEV ? '' : SHIPPED_MEASUREMENT_ID

/** Явное «выключить»: пустая строка этого больше не значит — см. ниже. */
const OFF = 'off'

/**
 * Как сходятся зашитое значение и переменная сборки.
 *
 * ПУСТАЯ ПЕРЕМЕННАЯ — ЭТО «НЕ ЗАДАНО», а не «выключить». Строчкой выше стояло
 * `env ?? FALLBACK`, и на этом первый же прод-деплой уехал без счётчика:
 * `docker-compose.prod.yml` передаёт в сборку
 * `VITE_ACHP_GA_MEASUREMENT_ID: "${ACHP_GA_MEASUREMENT_ID:-}"`, переменной CI
 * заведено не было, и в контейнер ушла ПУСТАЯ СТРОКА. Она задана, поэтому `??`
 * запасное значение не подставил, и бандл собрался без идентификатора —
 * молча, зелёным пайплайном, при исправном коде. Локально это не
 * воспроизводилось: там переменной нет вовсе, то есть `undefined`.
 *
 * Выключить аналитику осознанно теперь можно словом `off` — оно не похоже ни
 * на «не задано», ни на опечатку.
 */
export function resolveMeasurementId(override: string | undefined, shipped: string): string {
  const explicit = (override ?? '').trim()
  if (explicit.toLowerCase() === OFF) return ''
  const chosen = explicit || shipped
  return isMeasurementId(chosen) ? chosen : ''
}

/**
 * Настоящий ли это идентификатор. Отдельной функцией, потому что негодный ID
 * гасит аналитику МОЛЧА: опечатка в одну букву выглядит как исправный деплой, у
 * которого просто пусто в Realtime — и разбираться в этом приходится через
 * сутки. Тест держит зашитое значение под этой проверкой.
 */
export function isMeasurementId(value: string): boolean {
  return value !== PLACEHOLDER && SHAPE.test(value)
}

/** Measurement ID, если он настоящий, и пустая строка во всех прочих случаях. */
export const ACHP_GA_MEASUREMENT_ID: string = resolveMeasurementId(
  import.meta.env.VITE_ACHP_GA_MEASUREMENT_ID,
  FALLBACK,
)

/**
 * Ключ решения о согласии в `localStorage`.
 *
 * С именем приложения по той же причине, что и переменные сборки: домен
 * `hub4psy.com` живёт на хосте с соседями, а на телефоне разработчика — рядом с
 * их же `localhost`. Ключ `cookie-consent` рано или поздно означал бы «чьё-то
 * чужое согласие принято за наше».
 */
export const ACHP_CONSENT_STORAGE_KEY = 'achp.analytics.consent.v1'

/**
 * Собирать ли аналитику в этой среде.
 *
 * Три «нет», и каждое стоило бы дороже, чем выглядит:
 *
 *  1. **Нет идентификатора** — см. правило выше.
 *  2. **Нет браузера.** `scripts/prerender.mjs` прогоняет те же компоненты
 *     через `renderToString`: эффекты там не выполняются, но модуль всё равно
 *     импортируется, и обращение к `document` на сборке уронило бы её.
 *  3. **`navigator.webdriver`** — это сборочные и проверочные боты: Playwright
 *     в `npm run e2e`, снапшотилки, headless-прогон собранного бандла из
 *     предпушевой проверки. Они ходят по всем ключевым маршрутам подряд, и без
 *     этой строки каждый прогон рисовал бы в Realtime посетителя, который
 *     обошёл сайт за четыре секунды.
 */
export function analyticsAvailable(): boolean {
  if (!ACHP_GA_MEASUREMENT_ID) return false
  if (typeof window === 'undefined' || typeof document === 'undefined') return false
  if (typeof navigator !== 'undefined' && navigator.webdriver) return false
  return true
}
